import { Job, JobStatus, SupabaseJob } from '@applicator/shared'
import { statusToDbFormat } from '@applicator/shared'

import { supabase } from '../../lib/supabase'

import { updateSupabaseJobsWithDates, updateJobsStateWithDates } from './dateHandlers'
import { getNextRelevantJobId } from './navigation'
import { prepareStatusUpdateData, updateJobStatusOptimistically, updateSupabaseJobsAfterStatusChange, updateJobsStateAfterStatusChange } from './statusHandlers'

// Wrapper for handleDateChipsUpdate
export function createHandleDateChipsUpdateWrapper(
  currentJob: Job | null,
  supabaseJobs: SupabaseJob[],
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>
) {
  return async (updatedDates: Record<string, unknown>) => {
    if (currentJob?.id) {
      updateSupabaseJobsWithDates(currentJob.id, updatedDates, supabaseJobs, setSupabaseJobs)
      updateJobsStateWithDates(currentJob.id, updatedDates, jobs, supabaseJobs, setJobs)
    }
  }
}

// Handle status change
export async function handleStatusChange(
  currentJob: Job | null,
  newStatus: JobStatus,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  supabaseJobs: SupabaseJob[],
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>,
  setCloseJobDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id) return

  const updateData = await prepareStatusUpdateData(newStatus, currentJob)

  // Optimistic update
  const updatedJobs = updateJobStatusOptimistically(newStatus, updateData, jobs, currentJob)
  setJobs(updatedJobs)
  setCloseJobDropdownOpen(false)

  // Update in Supabase
  const { error } = await supabase
    .from('jobs')
    .update(updateData)
    .eq('id', currentJob.id)

  if (error) {
    // Revert on error
    setJobs(jobs)
    setError(error?.message || 'Failed to update status')
  } else {
    // Update supabaseJobs and jobs state with correct format
    updateSupabaseJobsAfterStatusChange(newStatus, updateData, supabaseJobs, currentJob, setSupabaseJobs)
    updateJobsStateAfterStatusChange(newStatus, updateData, jobs, currentJob, setJobs)
  }
}

// Handle add to jobs
export async function handleAddToJobs(
  currentJob: Job | null,
  searchQueryId: string | null,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  supabaseJobs: SupabaseJob[],
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>,
  jobSearchQuery: string,
  navigate: (path: string) => void,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id || !searchQueryId) return

  // Remove search_query_id and set status to "Applying" to move job from search query results to general jobs list
  const { error } = await supabase
    .from('jobs')
    .update({ 
      search_query_id: null,
      status: statusToDbFormat('Applying')
    })
    .eq('id', currentJob.id)

  if (error) {
    setError(error?.message || 'Failed to start applying')
    return
  }

  // Remove current job from local state (it will disappear from the left list)
  const updatedJobs = jobs.filter(job => job.id !== currentJob.id)
  setJobs(updatedJobs)
  
  // Also remove from supabaseJobs
  const updatedSupabaseJobs = supabaseJobs.filter(j => j.id !== currentJob.id)
  setSupabaseJobs(updatedSupabaseJobs)

  // Navigate to next relevant job or back to list
  const nextJobId = getNextRelevantJobId(currentJob, jobs, jobSearchQuery, supabaseJobs)
  if (nextJobId) {
    navigate(`/job/${nextJobId}?searchQueryId=${searchQueryId}`)
  } else {
    // No more relevant jobs, go back to list
    navigate(`/?searchQueryId=${searchQueryId}&relevanceFilter=relevant`)
  }
}

// Handle reject relevant
export async function handleRejectRelevant(
  currentJob: Job | null,
  searchQueryId: string | null,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  supabaseJobs: SupabaseJob[],
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>,
  jobSearchQuery: string,
  navigate: (path: string) => void,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id || !searchQueryId) return

  const reason = window.prompt('Why are you rejecting this relevant job?')
  if (!reason || !reason.trim()) {
    return
  }

  const { error } = await supabase
    .from('jobs')
    .update({
      rejected_from_relevant: true,
      rejected_reason: reason.trim(),
      rejected_at: new Date().toISOString(),
    })
    .eq('id', currentJob.id)

  if (error) {
    setError(error?.message || 'Failed to reject job')
    return
  }

  // Remove current job from local state (it will disappear from the left list)
  const updatedJobs = jobs.filter(job => job.id !== currentJob.id)
  setJobs(updatedJobs)

  // Also remove from supabaseJobs
  const updatedSupabaseJobs = supabaseJobs.filter(j => j.id !== currentJob.id)
  setSupabaseJobs(updatedSupabaseJobs)

  // Navigate to next relevant job or back to list
  const nextJobId = getNextRelevantJobId(currentJob, jobs, jobSearchQuery, supabaseJobs)
  if (nextJobId) {
    navigate(`/job/${nextJobId}?searchQueryId=${searchQueryId}`)
  } else {
    navigate(`/?searchQueryId=${searchQueryId}&relevanceFilter=relevant`)
  }
}

// Handle delete job
export async function handleDeleteJob(
  currentJob: Job | null,
  searchQueryId: string | null,
  jobs: Job[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  setCloseJobDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>,
  navigate: (path: string) => void,
  setError: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (!currentJob || !currentJob.id) return

  // Delete from Supabase
  const { error } = await supabase
    .from('jobs')
    .delete()
    .eq('id', currentJob.id)

  if (error) {
    setError(error?.message || 'Failed to delete job')
    return
  }

  // Remove from local state
  const updatedJobs = jobs.filter(job => job.id !== currentJob.id)
  setJobs(updatedJobs)
  setCloseJobDropdownOpen(false)

  // Navigate back to jobs list
  const backUrl = searchQueryId 
    ? `/?searchQueryId=${searchQueryId}`
    : '/'
  navigate(backUrl)
}

// Handle job select
export function handleJobSelect(
  jobId: string,
  selectedJobId: string | null,
  searchQueryId: string | null,
  setSelectedJobId: React.Dispatch<React.SetStateAction<string | null>>,
  setEditingPosition: React.Dispatch<React.SetStateAction<boolean>>,
  setEditingCompany: React.Dispatch<React.SetStateAction<boolean>>,
  setEditingSalary: React.Dispatch<React.SetStateAction<boolean>>,
  setCloseJobDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setIsTransitioning: React.Dispatch<React.SetStateAction<boolean>>,
  detailContentRef: React.RefObject<HTMLDivElement>
) {
  if (selectedJobId === jobId) return

  // Сбрасываем состояние редактирования при смене вакансии
  setEditingPosition(false)
  setEditingCompany(false)
  setEditingSalary(false)
  setCloseJobDropdownOpen(false)

  // Немедленно обновляем состояние без навигации (чтобы избежать перезагрузки компонента)
  setSelectedJobId(jobId)
  
  // Обновляем URL без перезагрузки компонента через window.history
  const url = searchQueryId 
    ? `/job/${jobId}?searchQueryId=${searchQueryId}`
    : `/job/${jobId}`
  window.history.replaceState({}, '', url)

  // Запускаем анимацию перехода
  setIsTransitioning(true)
  
  // Плавно скрываем текущий контент
  if (detailContentRef.current) {
    detailContentRef.current.style.opacity = '0'
    detailContentRef.current.style.transform = 'translateX(20px)'
  }

  // Плавно показываем новый контент после небольшой задержки
  setTimeout(() => {
    if (detailContentRef.current) {
      detailContentRef.current.style.opacity = '1'
      detailContentRef.current.style.transform = 'translateX(0)'
    }
    setIsTransitioning(false)
  }, 150)
}
